import React, { useState } from 'react';

interface LikertScalePreviewProps {
  scale: 3 | 5 | 10;
  leftLabel?: string;
  rightLabel?: string;
  interactive?: boolean;
}

/**
 * LikertScalePreview — horizontal grid of circles representing the likert
 * scale. Used inside `LikertScaleItem` as a live preview for the author.
 */
export default function LikertScalePreview({
  scale,
  leftLabel,
  rightLabel,
  interactive = false,
}: LikertScalePreviewProps) {
  const [hovered, setHovered] = useState<number | null>(null);
  const values = Array.from({ length: scale }, (_, i) => i + 1);
  const gapClass = scale === 10 ? 'gap-1.5' : 'gap-3';
  const maxWidthClass = scale === 10 ? 'max-w-9' : 'max-w-11';

  return (
    <div className="flex flex-col gap-1.5">
      <div
        role="group"
        aria-label={`Likert scale preview, ${scale} points`}
        className={`flex ${gapClass} justify-between w-full`}
      >
        {values.map((v) => {
          const active = hovered !== null ? v <= hovered : false;
          const stateClass = active
            ? 'border-primary-500 bg-primary-500/10 text-primary-500'
            : 'border-text-disabled bg-transparent text-text-secondary';
          return (
            <button
              key={v}
              type="button"
              onMouseEnter={() => interactive && setHovered(v)}
              onMouseLeave={() => interactive && setHovered(null)}
              disabled={!interactive}
              aria-label={`${v} of ${scale}`}
              className={`flex-1 aspect-square ${maxWidthClass} rounded-full border-2 font-sans font-semibold text-[13px] transition-all duration-150 ease-out ${stateClass} ${interactive ? 'cursor-pointer' : 'cursor-default'}`}
            >
              {v}
            </button>
          );
        })}
      </div>
      {(leftLabel || rightLabel) && (
        <div className="flex justify-between font-sans font-medium text-[11px] text-text-secondary">
          <span>{leftLabel}</span>
          <span>{rightLabel}</span>
        </div>
      )}
    </div>
  );
}
